import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Archive, Monitor, FolderOpen } from "@phosphor-icons/react";
import { useLang } from "../../context/LangContext";
import { STRIPE_LINKS } from "../../lib/stripeLinks";
import { PricingExtras } from "./PricingExtras";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

const ARCHIVE_IDS = ["base", "pro", "fascicolo"];
const ARCHIVE_LINKS = [STRIPE_LINKS.archiveBase, STRIPE_LINKS.archivePro, STRIPE_LINKS.fascicoloPro];

export const Pricing = () => {
  const { t } = useLang();
  const p = t.pricing;
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="bg-white py-24 lg:py-32" data-testid="pricing">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="max-w-2xl">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-mono-tech text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600">
            [ {p.overline} ]
          </motion.p>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={reveal} className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#0F172A]">
            {p.title}
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={reveal} className="mt-5 text-base lg:text-lg leading-relaxed text-slate-600">
            {p.subtitle}
          </motion.p>
        </div>

        {/* PARTE 1: Abbonamento Piattaforma */}
        <motion.div custom={0} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="mt-16">
          <div className="flex items-center gap-2.5">
            <Monitor size={20} className="text-emerald-600" />
            <h3 className="font-display font-bold text-xl text-[#0F172A]">{p.platform.heading}</h3>
          </div>
          <p className="mt-1.5 text-sm text-slate-500">{p.platform.desc}</p>

          <div className="mt-6 max-w-2xl rounded-md bg-[#0A0F1A] border border-emerald-500/40 p-8 text-white" data-testid="pricing-platform-card">
            <div className="inline-flex items-center rounded-full bg-white/10 p-1" data-testid="pricing-billing-switch">
              <button onClick={() => setAnnual(false)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${!annual ? "bg-emerald-500 text-[#04170F]" : "text-slate-300"}`}
                data-testid="pricing-switch-monthly">{p.platform.monthly}</button>
              <button onClick={() => setAnnual(true)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${annual ? "bg-emerald-500 text-[#04170F]" : "text-slate-300"}`}
                data-testid="pricing-switch-annual">{p.platform.annual}</button>
            </div>

            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-display font-bold text-5xl" data-testid="pricing-platform-price">{annual ? p.platform.priceAnnual : p.platform.priceMonthly}</span>
              <span className="text-sm text-slate-400">{annual ? p.platform.perYear : p.platform.perMonth}</span>
              {annual && (
                <span className="ml-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-3 py-1 font-mono-tech text-[10px] font-bold uppercase tracking-wider text-emerald-400" data-testid="pricing-annual-badge">{p.platform.annualBadge}</span>
              )}
            </div>

            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {p.platform.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <Check size={16} weight="bold" className="text-emerald-400 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>

            <a href={annual ? STRIPE_LINKS.annual : STRIPE_LINKS.monthly} target="_blank" rel="noreferrer"
              className="mt-8 inline-flex w-full justify-center rounded-full bg-emerald-500 py-3.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors"
              data-testid={annual ? "pricing-cta-annual" : "pricing-cta-monthly"}>
              {p.platform.cta}
            </a>
          </div>
        </motion.div>

        {/* PARTE 2: Pacchetti una tantum (3 colonne) */}
        <motion.div custom={1} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} className="mt-16">
          <div className="flex items-center gap-2.5">
            <Archive size={20} className="text-emerald-600" />
            <h3 className="font-display font-bold text-xl text-[#0F172A]">{p.archive.heading}</h3>
          </div>
          <p className="mt-1.5 text-sm text-slate-500">{p.archive.desc}</p>

          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {p.archive.plans.map((plan, i) => {
              const isFascicolo = ARCHIVE_IDS[i] === "fascicolo";
              return (
                <div key={plan.name}
                  className={`rounded-md p-7 flex flex-col ${isFascicolo ? "bg-[#0A0F1A] border-2 border-emerald-500/60 text-white" : "bg-[#F8F9FA] border border-slate-200"}`}
                  data-testid={`pricing-archive-${ARCHIVE_IDS[i]}`}>
                  <div className="flex items-center justify-between">
                    <span className={`rounded-full px-3 py-1 font-mono-tech text-[10px] font-bold uppercase tracking-wider ${isFascicolo ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-400" : "bg-slate-200 text-slate-600"}`}>{p.archive.oneTime}</span>
                    {isFascicolo && <FolderOpen size={20} className="text-emerald-400" />}
                  </div>
                  <h4 className={`mt-4 font-display font-bold text-lg ${isFascicolo ? "" : "text-[#0F172A]"}`}>{plan.name}</h4>
                  <div className="mt-4 flex items-baseline gap-1.5">
                    <span className={`font-display font-bold text-4xl ${isFascicolo ? "" : "text-[#0F172A]"}`}>{plan.price}</span>
                    <span className={`text-xs lowercase ${isFascicolo ? "text-slate-400" : "text-slate-500"}`}>{p.archive.oneTime}</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2.5 text-sm ${isFascicolo ? "text-slate-300" : "text-slate-600"}`}>
                        <Check size={16} weight="bold" className={`shrink-0 mt-0.5 ${isFascicolo ? "text-emerald-400" : "text-emerald-500"}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a href={ARCHIVE_LINKS[i]} target="_blank" rel="noreferrer"
                    className={`mt-7 inline-flex w-full justify-center rounded-full py-3 text-sm font-semibold transition-colors ${isFascicolo ? "bg-emerald-500 text-[#04170F] hover:bg-emerald-400" : "bg-[#0A0F1A] text-white hover:bg-[#0F172A]"}`}
                    data-testid={`pricing-archive-${ARCHIVE_IDS[i]}-cta`}>
                    {p.archive.cta}
                  </a>
                </div>
              );
            })}
          </div>
        </motion.div>

        <PricingExtras />
      </div>
    </section>
  );
};
